<template>
  <div v-if="loading"><spinner /></div>
  <div v-else class="performance-wrapper">
    <div class="title-tabs-container">
      <h1 class="title-screen" style="font-size: 20px">{{ $adminT('performances.title', 'Performances') }}</h1>
      <div class="tabs-container">
        <button
          type="button"
          class="tab"
          :aria-pressed="activeStep === 1"
          :style="activeStep === 1 && 'background: #0277BD; color: white'"
          @click="handleShowLiveEvents()"
        >
          {{ $adminT('performances.live', 'Live') }}
        </button>
        <button
          type="button"
          class="tab"
          :aria-pressed="activeStep === 2"
          :style="activeStep === 2 && 'background: #0277BD; color: white'"
          @click="handleShowPastEvents()"
        >
          {{ $adminT('performances.past', 'Past') }}
        </button>
      </div>
    </div>
    <div v-for="(performance, index) in $v.form.$each.$iter" :key="performance.id">
      <performance
        v-if="!performance.deletedAt"
        :initial-values="performance"
        :index="parseInt(index)"
        :sold-at-least-once="performance.$model.soldAtLeastOnce || activeStep === 2"
        :sold-out="performance.$model.soldOut"
        :order-item-template-ids-sold-at-least-once="
          performance.$model.orderItemTemplateIdsSoldAtLeastOnce
        "
        :organization-slug="organizationSlug"
        :venues="venues"
        :new-event-modal="false"
        :is-canceled="performance.$model.canceled"
        :ticket-types="ticketTypes"
        :promo-codes="promoCodes"
        :vs="performance"
        :ticket-sellers="ticketSellers"
        :active-subscriptions="activeSubscriptions"
        @duplicatePerformance="handleDuplicatePerformance"
        @refetchOrderItemAndVenue="handleRefetchOrderItemAndVenue"
        @markPerformanceSoldOut="markPerformanceSoldOut"
        @resumePerformanceSales="resumePerformanceSales"
        @cancelPerformance="cancelPerformance"
        @removePerformance="removePerformance"
        @removeSelectedTicketSellers="removeSelectedTicketSellers($event)"
      />
    </div>
    <main-button v-if="activeStep === 1" style="margin-top: 8px" @click="addPerformance">
      <span class="add-symbol">+</span>{{ $adminT('performances.addDate', 'Add date') }}
    </main-button>
    <theater-add-tax-panel
      v-if="vatInfoLoaded && activeStep === 1 && hasVisiblePerformances"
      v-model="vatPercentage"
      :disabled="templateHasSoldTickets"
    />
    <div
      v-if="Object.keys($v.form.$each.$iter).length"
      class="buttons-wrapper"
      style="margin-top: 58px"
    >
      <main-button :text="$adminT('performances.cancel', 'CANCEL')" flat depressed @click="goBack()" />
      <div v-if="isPublished && allowPublishEventsToLanding" class="buttons">
        <v-tooltip top color="rgba(51, 51, 51, 0.80)" max-width="10rem">
          {{ $adminT('performances.saveHint', 'Save your progress without publishing the changes.') }}
          <main-button slot="activator" :text="$adminT('performances.saveDraft', 'Save draft')" outline @click="updatePerformance()" />
        </v-tooltip>
        <v-tooltip top color="rgba(51, 51, 51, 0.80)" max-width="10rem">
          {{ $adminT('performances.publishHint', 'Apply and make the changes visible to users.') }}
          <main-button
            slot="activator"
            :text="$adminT('performances.publish', 'Publish changes')"
            :loading="publishingInProgress"
            @click="updatePerformance(true)"
          />
        </v-tooltip>
      </div>
      <div v-else class="buttons">
        <main-button :text="$adminT('performances.update', 'UPDATE')" @click="updatePerformance()" />
      </div>
    </div>
    <simple-snackbar
      :open.sync="snackbarOpen"
      :message-prop="workflowLabel(localizedSnackbarMessage)"
      :color="snackbarColor"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import performance from './performance.vue'
import moment from 'moment'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import TheaterAddTaxPanel from './TheaterAddTaxPanel.vue'
import {
  EVENT_TEMPLATE_HAS_SOLD_TICKETS,
  GET_ORGANIZATION_EVENT_TEMPLATE,
} from '@/graphql/eventTemplate.graphql'
import { GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_PROMO_CODES } from '@/graphql/orderItemTemplate.graphql'
import {
  EVENT_CANCELED,
  EVENT_AVAILABLE,
  EVENT_SOLD_OUT,
  GET_EVENTS_WITH_TICKET_SELLER_ORGANIZATIONS_BY_EVENT_TEMPLATE_ID,
  PUBLISH_EVENT,
  UPDATE_EVENT_WITH_EVENT_TEMPLATE_ID,
} from '@/graphql/event.graphql'
import Spinner from '../shared/spinner.vue'
import { minLength, required } from 'vuelidate/lib/validators'
import { v4 as uuid } from 'uuid'
import { omit } from 'lodash'
import { GET_NON_BOX_OFFICE_TICKET_SELLERS } from '@/graphql/user.graphql'
import { uniq } from 'lodash'
import { EventStatus } from '@/types'
import { mapGetters } from 'vuex'
import { createMinValueValidator } from '../../utils'
import { getTheaterSubscriptions } from '@/graphql/theater-subscriptions.graphql'

export default {
  mixins: [WorkflowDisplay],
  name: 'performance-container',
  components: {
    performance,
    Spinner,
    SimpleSnackbar,
    TheaterAddTaxPanel,
  },
  props: {
    organizationSlug: String,
    eventTemplateId: String,
  },
  data() {
    return {
      form: {},
      venues: [],
      activeStep: 1,
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarColor: 'primary',
      allPerformances: [],
      loading: true,
      ticketTypes: [],
      promoCodes: [],
      ticketSellers: [],
      isPublished: false,
      publishingInProgress: false,
      activeSubscriptions: [],
      vatInfoLoaded: false,
      vatPercentage: null,
      templateHasSoldTickets: false,
    }
  },
  computed: {
    localizedSnackbarMessage() {
      switch (this.snackbarMessage) {
      case 'performances.error':
        return this.$adminT('performances.error', 'Oops, Something went wrong!')
      case 'performances.updateSuccess':
        return this.$adminT('performances.updateSuccess', 'Event updated successfully!')
      case 'performances.cancelSuccess':
        return this.$adminT('performances.cancelSuccess', 'The performance has been successfully canceled.')
      case 'performances.resumeSuccess':
        return this.$adminT('performances.resumeSuccess', 'The performance sales have been resumed successfully.')
      case 'performances.soldSuccess':
        return this.$adminT('performances.soldSuccess', 'The performance has been successfully marked as sold out.')
      default:
        return ''
      }
    },

    ...mapGetters('auth', ['user']),
    allowPublishEventsToLanding() {
      return this.user && this.user.flags && this.user.flags.allowPublishEventsToLanding
    },
    // Mirrors the performance list rendering above: entries of the active tab minus deleted ones.
    hasVisiblePerformances() {
      return Object.values(this.$v.form.$each.$iter).some(performance => !performance.deletedAt)
    },
  },
  async mounted() {
    await this.fetchTicketTypesAndVenues()
    await this.fetchEvents()
    await this.fetchTicketSellers()
    await this.fetchActiveSubscriptions()
    await this.fetchVatInfo()
  },
  validations() {
    return {
      form: {
        $each: {
          date: { required },
          time: { required },
          performanceType: { required },
          venueId: { required },
          audienceCapacity: {
            minValueDynamic: createMinValueValidator('totalTicketsSold'),
            required(v, obj) {
              let hasNoNumeration = !!this.venues.find(
                v => v.id === obj.venueId && (!v.seats || !v.predefinedSections),
              )

              return hasNoNumeration ? required(v) : required(!v)
            },
          },
          additionalCapacity: {
            minValueDynamic: createMinValueValidator('totalAdditionalCapacityTicketsSold'),
            required(v, obj) {
              let hasNoNumeration = !!this.venues.find(
                v => v.id === obj.venueId && (!v.seats || !v.predefinedSections),
              )

              return hasNoNumeration && obj.addExtraSeats ? required(v) : required(!v)
            },
            minValue: (value, p) => !p.addExtraSeats || value >= 1,
          },
          orderItemTemplateIds: {
            minLength: minLength(1),
            required,
          },
        },
      },
    }
  },
  methods: {
    handleShowPastEvents() {
      this.activeStep = 2

      const pastPerformances = this.allPerformances
        .filter(performance => moment(performance.to).isBefore(moment()))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      this.form = pastPerformances

      return
    },
    handleShowLiveEvents() {
      this.activeStep = 1

      const livePerformances = this.allPerformances
        .filter(performance => moment(performance.to).isAfter(moment()))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      this.form = livePerformances

      return
    },
    handleDuplicatePerformance(performanceData) {
      const rest = omit(performanceData, [
        'id',
        'soldOut',
        'soldAtLeastOnce',
        'orderItemTemplateIdsSoldAtLeastOnce',
        'eventCode',
      ])
      this.form.push({
        ...rest,
        id: uuid(),
        addExtraSeats: !!rest.additionalCapacity,
      })
    },
    async handleRefetchOrderItemAndVenue() {
      await this.fetchTicketTypesAndVenues()
      await this.fetchActiveSubscriptions()
    },
    addPerformance() {
      this.form.push({
        id: uuid(),
        orderItemTemplateIds: [],
        addExtraSeats: false,
        metadata: { theaterOnlineSales: true },
      })
    },
    removePerformance(id) {
      this.form = this.form.filter(perf => perf.id !== id)
    },
    async publishEventInBackground() {
      if (!this.isPublished) {
        return
      }

      try {
        await this.$apollo.mutate({
          mutation: PUBLISH_EVENT,
          variables: {
            eventTemplateId: this.eventTemplateId,
          },
        })
      } catch {
        return
      }
    },
    async markPerformanceSoldOut(id) {
      const { errors } = await this.$apollo.mutate({
        mutation: EVENT_SOLD_OUT,
        variables: {
          id,
          organizationSlug: this.organizationSlug,
        },
      })

      if (errors) {
        this.snackbarOpen = true
        this.snackbarMessage = 'performances.error'
        this.snackbarColor = 'error'

        return
      }

      await this.publishEventInBackground()
      this.snackbarOpen = true
      this.snackbarMessage = 'performances.soldSuccess'
      this.snackbarColor = 'primary'
      await this.fetchEvents()
    },
    async resumePerformanceSales(id) {
      const { errors } = await this.$apollo.mutate({
        mutation: EVENT_AVAILABLE,
        variables: {
          id,
          organizationSlug: this.organizationSlug,
        },
      })

      if (errors) {
        this.snackbarOpen = true
        this.snackbarMessage = 'performances.error'
        this.snackbarColor = 'error'

        return
      }

      await this.publishEventInBackground()
      this.snackbarOpen = true
      this.snackbarMessage = 'performances.resumeSuccess'
      this.snackbarColor = 'primary'
      await this.fetchEvents()
    },
    async cancelPerformance(id) {
      const { errors } = await this.$apollo.mutate({
        mutation: EVENT_CANCELED,
        variables: {
          id,
          organizationSlug: this.organizationSlug,
        },
      })

      if (errors) {
        this.snackbarOpen = true
        this.snackbarMessage = 'performances.error'
        this.snackbarColor = 'error'

        return
      }

      await this.publishEventInBackground()
      this.snackbarOpen = true
      this.snackbarMessage = 'performances.cancelSuccess'
      this.snackbarColor = 'primary'
      await this.fetchEvents()
    },
    modifyPerformanceData() {
      return this.form.map(event => {
        const { date, time } = event
        const rest = omit(event, [
          'date',
          'time',
          'createdAt',
          'soldOut',
          'soldAtLeastOnce',
          'totalAdditionalCapacityTicketsSold',
          'totalTicketsSold',
          'orderItemTemplateIdsSoldAtLeastOnce',
          'eventCode',
          'hasTheaterSubscriptionSales',
          'addExtraSeats',
          'canceled',
          '__typename',
        ])
        const dateOnly = moment(date).format('DD.MM.YYYY')
        const fromIso = moment(`${dateOnly} ${time}`, 'DD.MM.YYYY HH:mm').toISOString()

        return { ...rest, from: fromIso }
      })
    },
    removePromoCodesFromPerformancesWithoutRegularTicket() {
      const allOrganizationRegularTicketIds = this.ticketTypes
        .filter(t => !t.membership && !t.ticketSeller && t.prepaidCoupon)
        .map(t => t.id)
      this.form = this.form.map(p => {
        const selectedTicketTypesIncludeRegularTicket = p.orderItemTemplateIds.some(id =>
          allOrganizationRegularTicketIds.includes(id),
        )
        if (!selectedTicketTypesIncludeRegularTicket) {
          p.promoCodeIds = []
        }

        return p
      })
    },
    async updatePerformance(publishChanges = false) {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }
      if (publishChanges) {
        this.publishingInProgress = true
      }
      this.removePromoCodesFromPerformancesWithoutRegularTicket()
      const eventInput = this.modifyPerformanceData()
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_EVENT_WITH_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
          eventInput,
          publishChanges,
          VATPercentage:
            this.vatInfoLoaded && !this.templateHasSoldTickets ? this.vatPercentage : undefined,
        },
      })
      this.publishingInProgress = false
      if (errors) {
        this.snackbarOpen = true
        this.snackbarMessage =
          'performances.error'
        this.snackbarColor = 'error'

        return
      }
      this.snackbarOpen = true
      this.snackbarMessage = 'performances.updateSuccess'
      this.snackbarColor = 'primary'
      this.allPerformances = []
      await this.fetchEvents()
    },
    async fetchTicketTypesAndVenues() {
      const {
        data: {
          getOrganizationOrderItemTemplates,
          getOrganizationVenues,
          getAllOrganizationPromoCodes,
        },
        errors,
      } = await this.$apollo.query({
        query: GET_ORGANIZATION_ORDER_ITEM_TEMPLATES_AND_VENUES_AND_PROMO_CODES,
        variables: {
          organizationSlug: this.organizationSlug,
          pagination: this.pagination,
          includeArchived: true,
        },
      })

      if (errors) {
        this.snackbarOpen = true
        this.snackbarMessage = 'performances.error'
        this.snackbarColor = 'error'
      } else {
        this.venues = getOrganizationVenues.organizationVenues.map(venue => venue.venue)
        this.ticketTypes = getOrganizationOrderItemTemplates
        this.promoCodes = getAllOrganizationPromoCodes
      }
    },
    async fetchEvents() {
      const {
        data: { getEventsWithTicketSellerOrganizationsByEventTemplateId },
        errors,
      } = await this.$apollo.query({
        query: GET_EVENTS_WITH_TICKET_SELLER_ORGANIZATIONS_BY_EVENT_TEMPLATE_ID,
        variables: {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        },
      })

      if (errors) {
        this.snackbarOpen = true
        this.snackbarMessage = 'performances.error'
        this.snackbarColor = 'error'
      } else {
        this.allPerformances = getEventsWithTicketSellerOrganizationsByEventTemplateId.map(
          event => {
            return {
              ...omit(event, ['slug', 'orderItems', 'title', 'location', 'status']),
              addExtraSeats: !!event.additionalCapacity,
              additionalCapacity: event.additionalCapacity,
              venueId: event.venueId || this.venues.find(v => v.name === event.location.venue).id,
              date: event.from,
              totalTicketsSold: event.totalTicketsSold,
              totalAdditionalCapacityTicketsSold: event.totalAdditionalCapacityTicketsSold,
              soldOut: event.soldOut,
              canceled: event.canceled,
              metadata: event.metadata || { theaterOnlineSales: true },
              soldAtLeastOnce:
                !!event.hasTheaterSubscriptionSales ||
                event.orderItems.some(oi => parseInt(oi.quantity) !== oi.initialQuantity),
              orderItemTemplateIdsSoldAtLeastOnce: event.orderItems
                .filter(oi => parseInt(oi.quantity) !== oi.initialQuantity)
                .map(oi => parseInt(oi.orderItemTemplateId)),
              time: `${moment(event.from).hours()}:${moment(event.from).minutes()}`,
              isIncludedInSubscription: event.isIncludedInSubscription,
              orderItemTemplateIds: event.orderItems.map(oi => parseInt(oi.orderItemTemplateId)),
              ticketSellerIds: uniq(
                event.orderItems
                  .map(oi => oi.ticketSellerOrganizations)
                  .flat()
                  .map(tso => parseInt(tso.id)),
              ),
            }
          },
        )
        if (this.activeStep === 2) {
          this.handleShowPastEvents()
        } else {
          this.handleShowLiveEvents()
        }
        this.isPublished = getEventsWithTicketSellerOrganizationsByEventTemplateId.some(
          event => event.status === EventStatus.PUBLISHED,
        )
        this.loading = false
      }
    },
    async fetchTicketSellers() {
      const {
        data: { getNonBoxOfficeTicketSellers: ticketSellers },
      } = await this.$apollo.query({
        query: GET_NON_BOX_OFFICE_TICKET_SELLERS,
        variables: { organizationSlug: this.organizationSlug },
      })

      this.ticketSellers = ticketSellers.map(ts => {
        return {
          ...ts,
          id: Number(ts.id),
        }
      })
    },
    async fetchVatInfo() {
      try {
        const { data } = await this.$apollo.query({
          query: GET_ORGANIZATION_EVENT_TEMPLATE,
          variables: {
            organizationSlug: this.organizationSlug,
            eventTemplateId: this.eventTemplateId,
          },
        })
        const template = data && data.getOrganizationEventTemplate
        if (template) {
          const vatObligor = !!(template.organization && template.organization.VATObligor)
          // Legacy templates without a stored VAT default like on create: obligors 5%, others 0%.
          const fallback = vatObligor ? 5 : 0
          this.vatPercentage =
            template.VATPercentage != null ? Number(template.VATPercentage) : fallback
          if (![0, 5, 13, 25].includes(Number(this.vatPercentage))) {
            this.vatPercentage = fallback
          }
          const { data: soldData } = await this.$apollo.query({
            query: EVENT_TEMPLATE_HAS_SOLD_TICKETS,
            variables: {
              organizationSlug: this.organizationSlug,
              eventTemplateId: this.eventTemplateId,
            },
          })
          this.templateHasSoldTickets = !!(soldData && soldData.eventTemplateHasSoldTickets)
          this.vatInfoLoaded = true
        }
      } catch (e) {
        // Non-blocking: leave VAT panel hidden if info can't be fetched.
        this.vatInfoLoaded = false
      }
    },
    async fetchActiveSubscriptions() {
      try {
        const { data } = await this.$apollo.query({
          query: getTheaterSubscriptions,
          variables: { organizationSlug: this.organizationSlug },
          fetchPolicy: 'no-cache',
        })
        const list = data && data.getTheaterSubscriptions ? data.getTheaterSubscriptions : []
        const active = list.filter(s => {
          return !!s.active
        })
        this.activeSubscriptions = active
      } catch (e) {
        this.activeSubscriptions = []
      }
    },
    removeSelectedTicketSellers(performanceId) {
      const performance = this.form.find(item => item.id === performanceId)
      if (!performance) return

      performance.ticketSellerIds = undefined
    },
  },
}
</script>

<style scoped>
.performance-wrapper {
  max-width: 700px;
}
.tabs-container {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}
@media screen and (max-width: 800px) {
  .tabs-container {
    margin: 1rem 0;
  }
}
.tab {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
  color: #0277bd;
  min-width: 78px;
  min-height: 36px;
  padding: 8px 16px;
  white-space: nowrap;
  border: 0;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.title-tabs-container {
  justify-content: space-between;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.tab:focus-visible {
  outline: 2px solid #0277bd;
  outline-offset: 2px;
}
</style>
